import { AUTH_STORAGE_KEYS } from '../constants/auth.constants'
import type { MockUserRecord } from '../models/auth.model'

/**
 * Persistent mock user database backed by localStorage. Seeded with the five
 * demo accounts on first access so the app "just works" on a fresh browser.
 *
 * This is the single source of truth for the mock auth layer and is the only
 * module that should read/write USERS_DB. When a real backend replaces
 * auth.service, this file can be deleted entirely.
 */

const SEED_USERS: ReadonlyArray<Omit<MockUserRecord, 'id' | 'createdAt'>> = [
  {
    email: 'ramakrishna@instavc.com',
    name: 'Ramakrishna',
    password: 'Inmeet@123',
    role: 'admin',
  },
  {
    email: 'sandeep@instavc.com',
    name: 'Sandeep',
    password: 'Inmeet@123',
    role: 'member',
  },
  {
    email: 'naresh@instavc.com',
    name: 'Naresh',
    password: 'Inmeet@123',
    role: 'member',
  },
  {
    email: 'rp@instavc.com',
    name: 'RP',
    password: 'Inmeet@123',
    role: 'member',
  },
  {
    email: 'support@instavc.com',
    name: 'Support',
    password: 'Inmeet@123',
    role: 'support',
  },
]

const normaliseEmail = (email: string) => email.trim().toLowerCase()

const generateId = () =>
  `u_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`

const readAll = (): MockUserRecord[] => {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEYS.USERS_DB)
    if (!raw) return []
    const parsed = JSON.parse(raw) as MockUserRecord[]
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

const writeAll = (users: MockUserRecord[]) => {
  localStorage.setItem(AUTH_STORAGE_KEYS.USERS_DB, JSON.stringify(users))
}

const seedIfEmpty = () => {
  const existing = readAll()
  if (existing.length > 0) return existing
  const now = Date.now()
  const seeded: MockUserRecord[] = SEED_USERS.map((u) => ({
    ...u,
    email: normaliseEmail(u.email),
    id: generateId(),
    createdAt: now,
  }))
  writeAll(seeded)
  return seeded
}

export const mockUsersRepo = {
  all(): MockUserRecord[] {
    return seedIfEmpty()
  },

  findByEmail(email: string): MockUserRecord | undefined {
    const target = normaliseEmail(email)
    return seedIfEmpty().find((u) => u.email === target)
  },

  exists(email: string): boolean {
    return Boolean(this.findByEmail(email))
  },

  create(input: Omit<MockUserRecord, 'id' | 'createdAt'>): MockUserRecord {
    const users = seedIfEmpty()
    const record: MockUserRecord = {
      ...input,
      email: normaliseEmail(input.email),
      id: generateId(),
      createdAt: Date.now(),
    }
    users.push(record)
    writeAll(users)
    return record
  },

  updatePassword(email: string, newPassword: string): MockUserRecord | null {
    const users = seedIfEmpty()
    const target = normaliseEmail(email)
    const idx = users.findIndex((u) => u.email === target)
    if (idx === -1) return null
    users[idx] = { ...users[idx], password: newPassword }
    writeAll(users)
    return users[idx]
  },

  verifyPassword(email: string, password: string): boolean {
    const user = this.findByEmail(email)
    if (!user) return false
    return user.password === password
  },
}
